import React, { useState, useEffect } from 'react'
import {
  Heading,
  Typography,
  TextField,
  Box,
  Frame,
  Button,
  Divider
} from 'components'
import { updateUser, useContent } from 'utils'
import { fetchCanadianCities, searchCanadianCities } from 'utils/bipoc'
import { ProgressBarFooter } from './FreelancerProgressBarFooter'
import firebase from 'utils/firebase'
import { ONBOARD_SKIP_VALIDATION } from '../../../constants'
import { makeStyles, Chip } from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'

const useStyles = makeStyles((theme) => ({
  input: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3)
  },
  subheading: {
    fontSize: '1.1rem',
    fontWeight: 500,
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(2)
  },
  addButton: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(4)
  },
  citiesContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(3),
    gap: theme.spacing(1)
  },
  cityChip: {
    margin: theme.spacing(0.5)
  }
}))

const FreelancerSearchCity = ({
  onboarding,
  user,
  cities: propCities,
  stepIndex,
  totalSteps,
  onSubmit = () => { },
  onNext = () => { },
}) => {
  const classes = useStyles()
  const [selectedCity, setSelectedCity] = useState(null)
  const [cities, setCities] = useState([])
  const [loading, setLoading] = useState(false)
  const [searchInput, setSearchInput] = useState('')
  const [selectedCities, setSelectedCities] = useState([])
  // Reference to autocomplete component
  const autocompleteRef = React.useRef(null)
  // Reference to track if we've already initialized city data
  const initializedRef = React.useRef(false)
  const { landing } = useContent() || {}
  const buttonColor = landing?.['onboarding-button-color'] || 'primary'
  const buttonSecondaryColor = landing?.['onboarding-button-secondary'] || '#6c757d'

  // Memoize cityOptions to avoid re-creating on every render
  const cityOptions = React.useMemo(() => {
    return cities.length > 0
      ? cities.map(city => ({
        id: city.id,
        label: city.name,
        province: city.province
      }))
      : propCities?.map(city => ({ id: city.id, label: city.name })) ?? [];
  }, [cities, propCities])

  // Fetch cities on component mount if no cities are provided as props
  useEffect(() => {
    const loadCities = async () => {
      if (!propCities || propCities.length === 0) {
        setLoading(true)
        try {
          // Use the fetchCanadianCities function from bipoc.js
          const canadianCities = await fetchCanadianCities()
          setCities(canadianCities)
          // console.log(canadianCities)
        } catch (error) {
          console.error('Error loading Canadian cities:', error)
        } finally {
          setLoading(false)
        }
      }
    }

    loadCities()
  }, [propCities])

  // Search cities when user types in the search box
  useEffect(() => {
    const searchTimer = setTimeout(async () => {
      if (searchInput.trim().length > 1) {
        setLoading(true)
        try {
          // Use the searchCanadianCities function from bipoc.js
          const searchResults = await searchCanadianCities(searchInput)
          setCities(searchResults)
        } catch (error) {
          console.error('Error searching cities:', error)
        } finally {
          setLoading(false)
        }
      }
    }, 500) // Debounce search to avoid too many API calls

    return () => clearTimeout(searchTimer)
  }, [searchInput])

  // Initialize with user's city if exists - runs when user data and city options are available
  useEffect(() => {
    // Only run this effect if we have both user data and city options
    if (!user || cityOptions.length === 0) return;

    // Only initialize if we haven't done so already
    if (initializedRef.current) return;

    // Mark as initialized to prevent further runs
    initializedRef.current = true;

    // Only use the cities array format
    if (user?.cities && Array.isArray(user.cities) && user.cities.length > 0) {
      // If we have at least one city, set it as the selected city
      if (user.cities.length > 0) {
        // Try to find the first city in cityOptions
        const primaryCityMatch = cityOptions.find(c => c.label === user.cities[0]);

        // If we have a match, use it. Otherwise create a custom city with a unique ID
        const primaryCity = primaryCityMatch || {
          id: (user.cityIds && user.cityIds[0]) || `custom-${user.cities[0].replace(/\s+/g, '-').toLowerCase()}`,
          label: user.cities[0],
          province: ''
        };

        setSelectedCity(primaryCity);
      }

      // If we have additional cities (more than 1), add them to selectedCities
      if (user.cities.length > 1) {
        // Map the rest of the cities (excluding the first one which is already the selectedCity)
        const additionalCities = user.cities.slice(1).map((cityName, index) => {
          // Try to find the city in cityOptions
          const foundCity = cityOptions.find(c => c.label === cityName);

          // If we find a match, use it
          if (foundCity) return foundCity;

          // Otherwise create a custom city with a unique ID
          // Use cityIds if available, otherwise generate one
          const customId = user.cityIds && user.cityIds[index + 1]
            ? user.cityIds[index + 1]
            : `custom-${cityName.replace(/\s+/g, '-').toLowerCase()}-${index}`;

          return {
            id: customId,
            label: cityName,
            province: ''
          };
        });

        setSelectedCities(additionalCities);
      }
    }
    // No fallback to old format
  }, [user, cityOptions]) // Keep dependencies to ensure effect runs when they change, but use ref to prevent double initialization

  const handleCityChange = (event, newValue) => {
    setSelectedCity(newValue)
  }

  const handleSearchInputChange = (event, value) => {
    setSearchInput(value)
  }

  // Handle adding a city to the list
  const handleAddCity = () => {
    if (selectedCity && !selectedCities.some(city => city.id === selectedCity.id)) {
      // Add selected city to the array of cities
      setSelectedCities([...selectedCities, selectedCity])

      // Clear the selection
      setSelectedCity(null)
      setSearchInput('')

      // Focus the autocomplete input and open the dropdown
      if (autocompleteRef.current) {
        // Focus the input element
        const inputElement = autocompleteRef.current.getElementsByTagName('input')[0]
        if (inputElement) {
          setTimeout(() => {
            inputElement.focus()
            // Trigger a click on the input to open the dropdown
            inputElement.click()
          }, 100)
        }
      }
    }
  }

  // Handle removing a city from the list
  const handleRemoveCity = (cityToRemove) => {
    setSelectedCities(selectedCities.filter(city => city.id !== cityToRemove.id))
  }

  // Prepare city data for saving/update
  const prepareCityData = () => {
    const updateData = {}
    const allCities = []
    const allCityIds = []

    // Add the selected city if available
    if (selectedCity) {
      allCities.push(selectedCity.label)
      allCityIds.push(selectedCity.id)
    }

    // Add all cities from the selectedCities array
    if (selectedCities.length > 0) {
      selectedCities.forEach(city => {
        // Avoid duplicates
        if (!allCities.includes(city.label)) {
          allCities.push(city.label)
          allCityIds.push(city.id)
        }
      })
    }

    // Store all cities in a single array
    if (allCities.length > 0) {
      updateData.cities = allCities
      updateData.cityIds = allCityIds
    }

    return updateData;
  }

  // Handle the Next button click in onboarding flow
  const handleNext = async () => {
    // Allow progression if either there's a selected city or there are cities in the list
    const hasValidCitySelection = selectedCity || selectedCities.length > 0
    if (!ONBOARD_SKIP_VALIDATION && !hasValidCitySelection) return

    try {
      const updateData = prepareCityData();

      await updateUser(user, updateData)

      firebase.analytics().logEvent('user_freelancer_city_submit_click')

      if (onNext) {
        onNext()
      }
    } catch (error) {
      console.error('Error updating city:', error)
    }
  }

  // Handle the Save button click in edit mode
  const handleSave = async () => {
    // Validate city selection
    const hasValidCitySelection = selectedCity || selectedCities.length > 0
    if (!ONBOARD_SKIP_VALIDATION && !hasValidCitySelection) return

    try {
      const updateData = prepareCityData();

      await updateUser(user, updateData)

      firebase.analytics().logEvent('user_freelancer_city_edit_save')

      // Execute onSubmit callback if provided
      if (onSubmit) {
        onSubmit()
      }
    } catch (error) {
      console.error('Error saving city data:', error)
    }
  }

  return (
    <Frame maxWidth="xs" user={user}>
      <Box mx="auto" maxWidth={430}>
        <Heading>
          Choose your city.
          <br />
          Canada only.
        </Heading>

        <Typography variant="body1" style={{ marginBottom: 16 }}>
          Choosing a city implies your working area.
        </Typography>

        <Autocomplete
          id="city-selector"
          ref={autocompleteRef}
          options={cityOptions}
          getOptionLabel={(option) => option.label}
          getOptionSelected={(option, value) => option.id === value.id}
          value={selectedCity}
          onChange={handleCityChange}
          onInputChange={handleSearchInputChange}
          className={classes.input}
          loading={loading}
          inputValue={searchInput}
          renderOption={(option) => (
            <div>
              {option.label}
              {option.province && (
                <Typography variant="body2" color="textSecondary" style={{ marginLeft: 4 }}>
                  {option.province}
                </Typography>
              )}
            </div>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="City"
              variant="outlined"
              fullWidth
              required
              placeholder="Start typing your city"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <React.Fragment>
                    {loading ? (
                      <div className="MuiCircularProgress-root MuiCircularProgress-colorPrimary MuiCircularProgress-indeterminate"
                        style={{ width: 20, height: 20 }}
                        role="progressbar">
                        <svg className="MuiCircularProgress-svg" viewBox="22 22 44 44">
                          <circle
                            className="MuiCircularProgress-circle MuiCircularProgress-circleIndeterminate"
                            cx="44"
                            cy="44"
                            r="20.2"
                            fill="none"
                            strokeWidth="3.6"
                          />
                        </svg>
                      </div>
                    ) : null}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              }}
            />
          )}
        />

        <Typography className={classes.subheading}>
          Do you split your time between multiple cities?
        </Typography>

        <Button
          variant="contained"
          style={{
            'background-color': selectedCity ? buttonColor : buttonSecondaryColor,
            color: 'white',
            transition: 'background-color 0.3s ease' // Smooth transition between colors
          }}
          className={classes.addButton}
          onClick={handleAddCity}
          disabled={!selectedCity}
        >
          Add Another City
        </Button>

        {/* Display selected additional cities as chips */}
        {selectedCities.length > 0 && (
          <Box className={classes.citiesContainer}>
            {selectedCities.map((city) => (
              <Chip
                key={city.id}
                label={city.label}
                onDelete={() => handleRemoveCity(city)}
                className={classes.cityChip}
                color="primary"
                variant="outlined"
              />
            ))}
          </Box>
        )}

        <Divider />

        {onboarding ? (
          <Box mx="auto" my={3} maxWidth={280}>
            <Button
              variant="contained"
              type="submit"
              style={{ width: '100%', 'background-color': buttonColor }}
              onClick={handleNext}
              disabled={!ONBOARD_SKIP_VALIDATION && !selectedCity && selectedCities.length === 0}
            >
              Next
            </Button>
          </Box>
        ) : (
          <Box mx="auto" my={3} maxWidth={280}>
            <Button
              variant="contained"
              type="submit"
              style={{ width: '100%', 'background-color': buttonColor }}
              onClick={handleSave}
              disabled={!ONBOARD_SKIP_VALIDATION && !selectedCity && selectedCities.length === 0}
            >
              Save
            </Button>
          </Box>
        )}
      </Box>

      {onboarding && (
        <ProgressBarFooter
          valid={!!selectedCity || selectedCities.length > 0}
          stepIndex={stepIndex}
          firstStep={stepIndex}
          numSteps={totalSteps}
          onNext={handleNext}
          onPrev={() => { }}
          alwaysShowNav={ONBOARD_SKIP_VALIDATION}
        />
      )}
    </Frame>
  )
}

export default FreelancerSearchCity
